import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getCurrentUser, User } from '../services/authService';
import { canAccessBusinessSettings, getPermissionErrorMessage } from '../services/staffPermissionService';
import TopBar from '../components/TopBar';
import Button from '../components/Button';
import { APP_NAME } from '../constants';

interface Business {
  id: string;
  name: string;
  address: string;
  email: string;
  phone: string;
  website: string;
  status: string;
  logoUrl?: string;
}

const Container = styled.div`
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (min-width: 768px) {
    padding: 0 40px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
`;

const PageTitle = styled.h1`
  font-size: 38px;
  font-weight: 800;
  margin-bottom: 16px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Breadcrumbs = styled.div`
  display: flex;
  margin-bottom: 20px;
  font-size: 14px;
  color: #555;
  width: 100%;
  overflow-x: hidden;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const BreadcrumbLink = styled.span`
  cursor: pointer;
  white-space: nowrap;
  
  &:hover {
    text-decoration: underline;
  }
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 8px;
`;

const BreadcrumbText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100px;
  
  @media (max-width: 480px) {
    max-width: 80px;
  }
`;

const BusinessSection = styled.div`
  margin-bottom: 40px;
  width: 100%;
`;

const SettingCard = styled.div`
  background-color: #fff;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SettingInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
`;

const SettingName = styled.h3`
  font-size: 18px;
  color: #333;
  font-weight: normal !important;
  margin-bottom: 4px;
`;

const SettingDescription = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0;
`;

const ButtonContainer = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px 0;
`;

const PermissionErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 60px 20px;
  background-color: #f9f9f9;
  border-radius: 12px;
  margin-top: 40px;
`;

const PermissionErrorTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
`;

const PermissionErrorMessage = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 32px;
  max-width: 400px;
  line-height: 1.5;
`;

const BusinessSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { businessId } = useParams<{ businessId: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const [permissionError, setPermissionError] = useState<string>('');

  useEffect(() => {
    const fetchUserAndBusinessData = async () => {
      if (!businessId) return;

      try {
        setLoading(true);
        const userData = await getCurrentUser();
        setUser(userData);

        if (!userData?.id) {
          navigate('/login');
          return;
        }

        // Check if user has permission to access business settings
        const hasPermission = await canAccessBusinessSettings(userData.id, businessId);
        if (!hasPermission) {
          setPermissionError(getPermissionErrorMessage('business-settings'));
          setLoading(false);
          return;
        }
        
        // Fetch business details
        const businessDoc = await getDoc(doc(db, 'businesses', businessId));
        
        if (businessDoc.exists()) {
          setBusiness({
            id: businessDoc.id,
            ...businessDoc.data()
          } as Business);
        } else {
          console.error("Business not found");
          navigate('/businesses');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserAndBusinessData();
  }, [businessId, navigate]);

  const handleNavigateToMyAccount = () => {
    navigate('/dashboard');
  };

  const handleNavigateToMyBusinesses = () => {
    navigate('/businesses');
  };

  const handleEditProfile = () => {
    navigate(`/business/${businessId}/profile`);
  };

  const handleEditCreditPercentages = () => {
    navigate(`/business/${businessId}/credit-percentages`);
  };

  const handleManageStaff = () => {
    navigate(`/business/${businessId}/staff`);
  };

  // Handle loading state
  if (loading) {
    return (
      <Container>
        <ContentWrapper>
          <TopBar user={user} />
          <LoadingState>Loading...</LoadingState>
        </ContentWrapper>
      </Container>
    );
  }

  // Handle permission error
  if (permissionError) {
    return (
      <>
        <Helmet>
          <title>{APP_NAME} - Access Denied</title>
        </Helmet>
        <Container>
          <ContentWrapper>
            <TopBar user={user} />
            <PermissionErrorContainer>
              <PermissionErrorTitle>Access Denied</PermissionErrorTitle>
              <PermissionErrorMessage>{permissionError}</PermissionErrorMessage>
              <Button 
                text="Back to Businesses" 
                onClick={() => navigate('/businesses')}
                bgColor="#000"
                textColor="#fff"
                width="auto"
                minWidth="180px"
              />
            </PermissionErrorContainer>
          </ContentWrapper>
        </Container>
      </>
    );
  }

  // Redirect if no business is found
  if (!business) {
    navigate('/businesses');
    return null;
  }
  return (
    <>
      <Helmet>
        <title>{APP_NAME} - Business Settings</title>
        <meta name="description" content={`${APP_NAME} - Business Settings`} />
      </Helmet>
      <Container>
      <ContentWrapper>
        <TopBar user={user} />
        
        <PageTitle>{business.name}</PageTitle>
        
        <Breadcrumbs>
          <BreadcrumbLink onClick={handleNavigateToMyAccount}>My Account</BreadcrumbLink>
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
          <BreadcrumbLink onClick={handleNavigateToMyBusinesses}>Businesses</BreadcrumbLink>
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
          <BreadcrumbText>{business.name}</BreadcrumbText>
        </Breadcrumbs>
        
        <BusinessSection>
          {/* Business Profile Card */}
          <SettingCard onClick={handleEditProfile}>
            <SettingInfo>
              <SettingName>Business Profile</SettingName>
              <SettingDescription></SettingDescription>
            </SettingInfo>
            <ButtonContainer>
              {/* <Button 
                text="Edit" 
                onClick={handleEditProfile} 
                outline={true}
                bgColor="#000"
                textColor="#000"
                width="180px"
              /> */}
            </ButtonContainer>
          </SettingCard>
          
          {/* Store Credit Percentages Card */}
          <SettingCard onClick={handleEditCreditPercentages}>
            <SettingInfo>
              <SettingName>Percentages</SettingName>
              <SettingDescription></SettingDescription>
            </SettingInfo>
            <ButtonContainer>
              {/* <Button 
                text="Edit" 
                onClick={handleEditCreditPercentages} 
                outline={true}
                bgColor="#000"
                textColor="#000"
                width="180px"
              /> */}
            </ButtonContainer>
          </SettingCard>
          
          {/* Staff Management Card */}
          <SettingCard onClick={handleManageStaff}>
            <SettingInfo>
              <SettingName>Team</SettingName>
              <SettingDescription></SettingDescription>
            </SettingInfo>
            <ButtonContainer>
              {/* <Button 
                text="Edit" 
                onClick={handleManageStaff} 
                outline={true}
                bgColor="#000"
                textColor="#000"
                width="180px"
              /> */}
            </ButtonContainer>
          </SettingCard>        </BusinessSection>
      </ContentWrapper>
    </Container>
    </>
  );
};

export default BusinessSettingsPage;